import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@proaxiv/ui'
import type { ParticipantRole } from '../store/registrationsSlice'
import { Button } from './Button'
import { ROLE_STYLES } from './roleStyles'

// ── Icons ──────────────────────────────────────────────────────────────────────

const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)

// "Staff" is hidden from the frontend picker (backend/data model still support it for any
// existing participants) — only these three roles can be newly assigned via the UI.
const ACCEPT_ROLES: ParticipantRole[] = ['Audience', 'Speaker', 'Host']

interface Props {
  title: string
  /** Optional body copy shown under the title — e.g. extra context on a high-impact action. */
  description?: string
  notify: boolean
  rejectMessage?: string
  /** When set, show a role picker (used on accept) and include the chosen role in the result. */
  rolePicker?: boolean
  /** Initial role selection when {@link rolePicker} is enabled; defaults to {@code 'Audience'}. */
  initialRole?: ParticipantRole
  /** Set to `false` to hide the "Send notification" checkbox — e.g. for actions (like resend) that don't offer a notification choice. Defaults to `true`. */
  showNotifyCheckbox?: boolean
  onConfirm: (result: { notify: boolean; message?: string; role?: ParticipantRole }) => void
  onCancel: () => void
}

/** Modal dialog for confirming approval or rejection actions. */
export function ConfirmationModal({
  title,
  description,
  notify: initialNotify,
  rejectMessage,
  rolePicker = false,
  initialRole = 'Audience',
  showNotifyCheckbox = true,
  onConfirm,
  onCancel,
}: Props) {
  const { t } = useTranslation()
  const isReject = rejectMessage !== undefined
  const [notify, setNotify] = useState(initialNotify)
  const [message, setMessage] = useState(rejectMessage ?? '')
  const [role, setRole] = useState<ParticipantRole>(initialRole)

  return (
    <Modal labelledBy="modal-title">
      <div className="border-pax-purple/50 relative w-full max-w-md rounded-2xl border bg-white p-6 shadow-2xl ring-1 ring-black/5">
        <button
          type="button"
          onClick={onCancel}
          className="text-pax-gray hover:text-pax-black absolute top-4 right-4 cursor-pointer rounded-lg p-1 transition-colors focus-visible:outline-none"
          aria-label="Close"
        >
          <CloseIcon />
        </button>

        <h2
          id="modal-title"
          className={`text-pax-black pr-8 text-xl font-bold ${description ? 'mb-1.5' : 'mb-5'}`}
        >
          {title}
        </h2>

        {description && <p className="text-pax-gray mb-5 text-sm">{description}</p>}

        {isReject && (
          <div className="mb-4 flex flex-col gap-1">
            <label htmlFor="reject-message" className="text-pax-black text-sm font-medium">
              Rejection message
            </label>
            <textarea
              id="reject-message"
              className="border-pax-purple/40 bg-pax-light-purple text-pax-black focus:border-pax-blue min-h-[80px] w-full resize-y rounded-xl border px-3 py-2 text-sm transition-colors focus:outline-none"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('rejection_reason_placeholder')}
            />
          </div>
        )}

        {rolePicker && (
          <div className="mb-5 flex flex-col gap-1.5">
            <span className="text-pax-black text-sm font-medium">{t('accept_role_label')}</span>
            {/* Badge-styled options (matching the RoleBadge/RoleSelect pill look used elsewhere)
                instead of a native <select>, so the picker reads the same way as every other role
                control in the app. */}
            <div
              role="radiogroup"
              aria-label={t('accept_role_label')}
              className="border-pax-purple/40 bg-pax-light-purple flex flex-wrap gap-2 rounded-xl border p-2"
            >
              {ACCEPT_ROLES.map((r) => (
                <Button
                  key={r}
                  type="button"
                  variant="unstyled"
                  role="radio"
                  aria-checked={role === r}
                  onClick={() => setRole(r)}
                  className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-[10px] font-semibold tracking-wide uppercase transition-all ${ROLE_STYLES[r]} ${
                    role === r
                      ? 'ring-pax-blue ring-2 ring-offset-1'
                      : 'opacity-50 hover:opacity-100'
                  }`}
                >
                  {t(`role_${r.toLowerCase()}`)}
                </Button>
              ))}
            </div>
          </div>
        )}

        {showNotifyCheckbox && (
          <label className="mb-6 flex cursor-pointer items-center gap-2.5 text-sm select-none">
            <input
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              className="accent-pax-blue h-4 w-4 cursor-pointer"
            />
            <span className="text-pax-black">{t('send_notification')}</span>
          </label>
        )}

        <div
          className={`flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3 ${
            showNotifyCheckbox ? '' : 'mt-6'
          }`}
        >
          <Button variant="secondary" onClick={onCancel}>
            {t('btn_cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() =>
              onConfirm(isReject ? { notify, message } : rolePicker ? { notify, role } : { notify })
            }
          >
            {t('btn_confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
